import React, { Component, Children, PropTypes } from 'react';
import { addMessages } from 'oxygen-i18n';
import i18n from 'oxygen-i18n';
import moment from 'moment';

import IntlMessageFormat from 'intl-messageformat';
import IntlRelativeFormat from 'intl-relativeformat';
import createFormatCache from 'intl-format-cache';

require('moment/locale/sv');

function assertIsDate(date, errMsg) {
  // Determine if the `date` is valid by checking if it is finite, which is
  // the same way that `Intl.DateTimeFormat#format()` checks.
  if (!isFinite(date)) {
    throw new TypeError(errMsg);
  }
}

const typesSpec = {
  locales: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  formats: PropTypes.object,
  messages: PropTypes.object,
  getIntlMessage: PropTypes.func
};

export default class IntlProvider extends Component {
  static displayName = 'IntlProvider';

  static propTypes = typesSpec;
  static contextTypes = typesSpec;
  static childContextTypes = typesSpec;

  static filterFormatOptions = function(obj, defaults) {
    if (!defaults) {
      defaults = {};
    }

    return (this.formatOptions || []).reduce(function(opts, name) {
      if (obj.hasOwnProperty(name)) {
        opts[name] = obj[name];
      } else if (defaults.hasOwnProperty(name)) {
        opts[name] = defaults[name];
      }

      return opts;
    }, {});
  };

  getNumberFormat = createFormatCache(Intl.NumberFormat);
  getDateTimeFormat = createFormatCache(Intl.DateTimeFormat);
  getMessageFormat = createFormatCache(IntlMessageFormat);
  getRelativeFormat = createFormatCache(IntlRelativeFormat);

  constructor() {
    super(...arguments);
    moment.locale(this.props.locale.replace(/(_SE|_EN)/g, ''));
    this.setLocale(this.props.locale);
  }

  setLocale(locale) {
    try {
      const messages = require('../../../src/locale/messages.json');
      addMessages(messages);
      i18n.setLocale(locale.replace('_', '-'), locale.replace('_', '-'));
    } catch (e) {
      // Nothing going on
      //
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.locale !== this.props.locale) {
      this.setLocale(nextProps.locale);
    }
  }

  getChildContext() {
    const context = this.context;
    const props = this.props;
    return {
      locales: props.locales || context.locales,
      formats: props.formats || context.formats,
      messages: props.messages || context.messages,
      getIntlMessage: this.getIntlMessage.bind(this)
    };
  }

  formatDate(date, options) {
    date = new Date(date);
    // assertIsDate(date, 'A date or timestamp must be provided to formatDate()');
    return this._format('date', date, options);
  }

  formatTime(date, options) {
    date = new Date(date);
    // assertIsDate(date, 'A date or timestamp must be provided to formatTime()');
    return this._format('time', date, options);
  }

  formatRelative(date, options, formatOptions) {
    date = new Date(date);
    // assertIsDate(date, 'A date or timestamp must be provided to formatRelative()');
    return this._format('relative', date, options, formatOptions);
  }

  formatNumber(num, options) {
    return this._format('number', num, options);
  }

  formatMessage(message, values) {
    var locales = this.props.locales || this.context.locales;
    var formats = this.props.formats || this.context.formats;

    // When `message` is a function, assume it's an IntlMessageFormat
    // instance's `format()` method passed by reference, and call it. This
    // is possible because its `this` will be pre-bound to the instance.
    if (typeof message === 'function') {
      return message(values);
    }

    if (typeof message === 'string') {
      message = this.getMessageFormat(message, locales, formats);
    }

    return message.format(values);
  }

  getIntlMessage(path) {
    var messages = this.props.messages || this.context.messages;
    var pathParts = path.split('.');
    var message;

    try {
      message = pathParts.reduce(function(obj, pathPart) {
        return obj[pathPart];
      }, messages);
    } finally {
      if (message === undefined) {
        return path;
        //throw new ReferenceError('Could not find Intl message: ' + path);
      }
    }

    return message;
  }

  getNamedFormat(type, name) {
    var formats = this.props.formats || this.context.formats;
    var format = null;

    try {
      format = formats[type][name];
    } finally {
      if (!format) {
        throw new ReferenceError('No ' + type + ' format named: ' + name);
      }
    }

    return format;
  }

  _format(type, value, options, formatOptions) {
    var locales = this.props.locales || this.context.locales;

    if (options && typeof options === 'string') {
      options = this.getNamedFormat(type, options);
    }

    switch (type) {
      case 'date':
      case 'time':
        return this.getDateTimeFormat(locales, options).format(value);
      case 'number':
        return this.getNumberFormat(locales, options).format(value);
      case 'relative':
        return this.getRelativeFormat(locales, options).format(
          value,
          formatOptions
        );
      default:
        throw new Error('Unrecognized format type: ' + type);
    }
  }

  render() {
    return Children.only(this.props.children);
  }
}



// WEBPACK FOOTER //
// ./src/js/components/IntlProvider.js